import { useEffect } from 'react'
import { socket } from '../socket/client.js'
import { useGameStore } from '../store/gameStore.js'
import { useUIStore } from '../store/uiStore.js'
import { useAnimationStore } from '../store/animationStore.js'
import { useStatsStore } from '../store/statsStore.js'
import { useTheaterStore } from '../store/theaterStore.js'
import { trackEvent } from '../utils/analytics.js'
import type { AnimationEvent, AnimationType } from '../store/animationStore.js'
import type {
  GameStartEvent,
  ActionResultActorEvent,
  ActionResultObserverEvent,
  GameOverEvent,
  SonarAction,
  TorpedoAction,
  TerrainCell,
} from '@fleetduel/shared'
import { coordKey } from '@fleetduel/shared'

function resolveActorAnimationType(e: ActionResultActorEvent): AnimationType {
  if (e.action.type === 'sonar') return 'sonar'
  if (e.action.type === 'torpedo') return 'torpedo'
  if (e.results.length > 0 && e.results[0].hit) return 'strike-hit'
  return 'strike-miss'
}

function resolveObserverAnimationType(e: ActionResultObserverEvent): AnimationType {
  if (e.actionType === 'sonar') return 'sonar'
  if (e.actionType === 'torpedo') return 'torpedo'
  if (e.cellsTargeted.length > 0 && e.cellsTargeted[0].hit) return 'strike-hit'
  return 'strike-miss'
}

/**
 * Binds socket events to Zustand stores.
 * Action results are routed through the animation queue — store updates
 * are deferred until the animation completes (or is skipped).
 */
export function useSocket(): void {
  const store = useGameStore
  const uiStore = useUIStore
  const animStore = useAnimationStore

  useEffect(() => {
    socket.on('room_joined', (data: { roomId: string; ownTerrain?: TerrainCell[]; enemyTerrain?: TerrainCell[] }) => {
      store.getState().setRoomId(data.roomId)
      // Server-randomized terrain layouts (per game, per board)
      useTheaterStore.getState().setTerrainLayouts(data.ownTerrain ?? null, data.enemyTerrain ?? null)
    })

    socket.on('game_start', (event: GameStartEvent) => {
      store.getState().setPlayerIndex(event.yourIndex)
      store.getState().setPhase('IN_PROGRESS')
      store.getState().setCurrentTurn(event.firstTurn)
    })

    socket.on('action_result', (event: ActionResultActorEvent | ActionResultObserverEvent) => {
      if (event.type === 'action_result_actor') {
        const e = event as ActionResultActorEvent
        const animType = resolveActorAnimationType(e)

        // Record match stats (immediate — not gated on animations)
        const stats = useStatsStore.getState()
        stats.recordTurn()
        if (e.action.type === 'torpedo') stats.recordChargeUsed('torpedo')
        if (e.action.type === 'sonar') stats.recordChargeUsed('sonar')
        if (e.action.type !== 'sonar') {
          for (const r of e.results) stats.recordShot(r.hit)
        }
        if (e.sunkShip) stats.recordFirstBlood('player')

        const target = e.action.type === 'sonar'
          ? (e.action as SonarAction).center
          : (e.action as { target: typeof e.results[0]['coord'] }).target

        const animEvent: AnimationEvent = {
          type: animType,
          target,
          cells: e.results,
          direction: e.action.type === 'torpedo'
            ? (e.action as TorpedoAction).direction
            : undefined,
          sunkShip: e.sunkShip ?? undefined,
          sonarCount: e.sonarCount,
          isOwnBoard: false,
          onComplete: () => {
            store.getState().applyActorResult(
              e.results,
              e.sunkShip,
              e.torpedoCharges,
              e.sonarCharges,
              e.sonarCount,
            )
            if (e.sonarCount !== undefined) {
              uiStore.getState().setLastSonarCount(e.sonarCount)
              animStore.getState().addSonarBadge(coordKey(target), e.sonarCount)
            }

            if (e.sunkShip) {
              animStore.getState().enqueue({
                type: 'ship-sunk',
                target,
                sunkShip: e.sunkShip,
                cells: e.results,
                isOwnBoard: false,
                onComplete: () => {},
              })
            }
          },
        }

        animStore.getState().enqueue(animEvent)

        // Turn and phase can update immediately (not animation-dependent)
        store.getState().setCurrentTurn(e.nextTurn)
        if (e.phase) {
          store.getState().setPhase(e.phase)
        }
      } else {
        const e = event as ActionResultObserverEvent

        // Record match stats (immediate — not gated on animations)
        if (e.sunkShip) {
          const stats = useStatsStore.getState()
          stats.recordShipLost()
          stats.recordFirstBlood('opponent')
        }

        // Observer doesn't see sonar zone — skip animation for sonar
        if (e.actionType === 'sonar') {
          store.getState().applyObserverResult(e.actionType, e.cellsTargeted, e.sunkShip)
          store.getState().setCurrentTurn(e.nextTurn)
          if (e.phase) store.getState().setPhase(e.phase)
          return
        }

        const animType = resolveObserverAnimationType(e)
        const target = e.cellsTargeted.length > 0
          ? e.cellsTargeted[0].coord
          : { col: 'A' as const, row: 1 as const }

        const animEvent: AnimationEvent = {
          type: animType,
          target,
          cells: e.cellsTargeted,
          sunkShip: e.sunkShip ?? undefined,
          isOwnBoard: true,
          onComplete: () => {
            store.getState().applyObserverResult(e.actionType, e.cellsTargeted, e.sunkShip)

            if (e.sunkShip) {
              animStore.getState().enqueue({
                type: 'ship-sunk',
                target,
                sunkShip: e.sunkShip,
                cells: e.cellsTargeted,
                isOwnBoard: true,
                onComplete: () => {},
              })
            }
          },
        }

        animStore.getState().enqueue(animEvent)
        store.getState().setCurrentTurn(e.nextTurn)
        if (e.phase) store.getState().setPhase(e.phase)
      }
    })

    socket.on('game_over', (event: GameOverEvent) => {
      const stats = useStatsStore.getState()
      const won = event.winner === store.getState().playerIndex
      stats.recordGameEnd(won, stats.matchDifficulty)
      trackEvent('game_complete', {
        result: won ? 'win' : 'loss',
        theater: useTheaterStore.getState().theaterId,
        difficulty: stats.matchDifficulty,
        turns: stats.matchTurns,
        shots: stats.matchShots,
        hits: stats.matchHits,
        torpedoes_used: stats.matchTorpedoesUsed,
        sonar_used: stats.matchSonarUsed,
        ships_lost: stats.matchShipsLost,
      })
      store.getState().setGameOver(event.winner, event.boards, event.boardHits)
    })

    socket.on('opponent_ready', () => {})
    socket.on('opponent_disconnected', () => {})

    socket.on('error', (event: { message: string }) => {
      console.error('Server error:', event.message)
    })

    return () => {
      socket.off('room_joined')
      socket.off('game_start')
      socket.off('action_result')
      socket.off('game_over')
      socket.off('opponent_ready')
      socket.off('opponent_disconnected')
      socket.off('error')
    }
  }, [])
}
